// Fiche d'un paiement : transaction, notifications du prestataire et leur traitement (appliquée,
// ignorée en doublon, tardive, contradictoire, invalide), imputations, quittance, remboursements ;
// simulateur pour une transaction en attente, interrogation du prestataire, annulation motivée,
// proposition de remboursement.
import { Bandeau, Bouton, Lien, LienBouton, Panneau, PuceEtat } from '@cc/ui';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { dateHeure } from '../../commun/format';
import { LIBELLES_CANAL, LIBELLES_ETAT_REMBOURSEMENT, LIBELLES_STATUT_ANNONCE, LIBELLES_TRAITEMENT, francs, libelle, tonRemboursement, type Remboursement } from './donnees';
import { FormulaireAction } from './FormulaireAction';
import { NavigationFinances, useDroitsRecettes } from './NavigationFinances';
import { puces } from './PagePaiements';
import { usePaiement } from './requetes';
import { Simulateur } from './Simulateur';
import commun from '../../commun/commun.module.css';
import styles from './recettes.module.css';

function StatutPrestataire({ id }: { id: string }) {
  const [statut, setStatut] = useState<{ reference: string; statut: string; detail?: string | null } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  return (
    <div className={styles.pile}>
      <Bouton
        petit
        variante="discret"
        icone={RefreshCw}
        chargement={enCours}
        onClick={() => {
          setEnCours(true);
          setErreur(null);
          requete<{ reference: string; statut: string; detail?: string | null }>(`/recettes/paiements/${id}/statut-prestataire`)
            .then(setStatut)
            .catch((e: unknown) => setErreur(messageErreur(e)))
            .finally(() => setEnCours(false));
        }}
      >
        Interroger le prestataire
      </Bouton>
      {statut ? (
        <p role="status" className={styles.sous}>
          Statut annoncé par le prestataire : {statut.statut}
          {statut.detail ? ` (${statut.detail})` : ''}. Le serveur fait foi : seule une notification signée change l&apos;état.
        </p>
      ) : null}
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
    </div>
  );
}

export function PagePaiement() {
  const { id = '' } = useParams();
  const droits = useDroitsRecettes();
  const navigate = useNavigate();
  const fiche = usePaiement(id);
  const p = fiche.data;
  const enAttente = p ? p.etat === 'en_attente' || p.etat === 'initie' : false;
  const simulateur = p?.prestataire === 'simulateur' && p.canal === 'simulateur';
  const remboursable = p ? p.montant - p.remboursements.filter((r) => r.etat !== 'rejete').reduce((s, r) => s + r.montant, 0) : 0;

  return (
    <>
      <EnTetePage
        etiquette={p ? `Paiement, ${libelle(LIBELLES_CANAL, p.canal)}` : 'Paiement'}
        titre={p ? `Paiement ${p.reference_serveur}` : 'Paiement'}
        description={p ? <span className={commun.ligne}>{puces(p)}</span> : null}
        actions={
          <LienBouton href="/recettes/paiements" icone={ArrowLeft}>
            Paiements
          </LienBouton>
        }
      />
      <NavigationFinances />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} lot="L2-A" fonction="La fiche du paiement">
        {p ? (
          <div className={commun.deuxColonnes}>
            <div className={commun.pile}>
              <Panneau titre="Transaction">
                <dl className={commun.definitions}>
                  <dt>Montant</dt>
                  <dd className={`${styles.mono} ${styles.montantFort}`}>{francs(p.montant)}</dd>
                  <dt>Canal et prestataire</dt>
                  <dd>{libelle(LIBELLES_CANAL, p.canal)}, {p.prestataire}{p.reference_prestataire ? ` (référence ${p.reference_prestataire})` : ''}</dd>
                  <dt>Initié le</dt>
                  <dd>{dateHeure(p.initie_le)}</dd>
                  <dt>Confirmé le</dt>
                  <dd>{p.confirme_le ? `${dateHeure(p.confirme_le)} (reçu par le serveur le ${dateHeure(p.recu_serveur_le)})` : 'non confirmé'}</dd>
                  {p.refuse_le ? (
                    <>
                      <dt>Refusé le</dt>
                      <dd>{dateHeure(p.refuse_le)}{p.motif_refus ? ` : ${p.motif_refus}` : ''}</dd>
                    </>
                  ) : null}
                  <dt>Créances visées</dt>
                  <dd>
                    {p.creances_visees.map((c) => (
                      <span key={c}>
                        <Lien href={`/recettes/creances/${c}`}>Créance {c.slice(0, 8)}</Lien>{' '}
                      </span>
                    ))}
                  </dd>
                  <dt>Quittance</dt>
                  <dd>{p.quittance ? <><span className={styles.mono}>{p.quittance.numero}</span> ({p.quittance.etat === 'valide' ? 'valide' : 'annulée'}, code <span className={styles.codeVerification}>{p.quittance.code_verification}</span>)</> : 'aucune (émise à la confirmation)'}</dd>
                </dl>
                {p.en_anomalie ? <Bandeau type="attention">Ce paiement est marqué en anomalie : une anomalie ouverte le concerne (voir la vue Anomalies).</Bandeau> : null}
              </Panneau>
              <Panneau titre="Notifications du prestataire">
                {p.notifications.length === 0 ? <p className={commun.na}>Aucune notification reçue.</p> : null}
                <ul className={styles.liste}>
                  {p.notifications.map((n) => (
                    <li key={n.id}>
                      <span>
                        <strong>{libelle(LIBELLES_STATUT_ANNONCE, n.statut_annonce)}</strong>
                        <span className={styles.sous}>
                          {dateHeure(n.recue_le)}, {n.prestataire}{n.reference_prestataire ? ` ${n.reference_prestataire}` : ''}, signature {n.signature_valide ? 'valide' : 'invalide'}
                          {n.detail ? ` ; ${n.detail}` : ''}
                        </span>
                      </span>
                      <PuceEtat ton={n.traitement === 'appliquee' ? 'ok' : n.traitement === 'ignoree_doublon' ? 'neutre' : 'retard'} libelle={libelle(LIBELLES_TRAITEMENT, n.traitement)} />
                    </li>
                  ))}
                </ul>
              </Panneau>
              <Panneau titre="Imputations">
                {p.imputations.length === 0 ? <p className={commun.na}>Aucune imputation : le montant n&apos;est imputé qu&apos;à la confirmation, la plus ancienne échéance d&apos;abord.</p> : null}
                <ul className={styles.liste}>
                  {p.imputations.map((i, k) => (
                    <li key={k}>
                      <span>
                        <Lien href={`/recettes/creances/${i.creance_id}`} className={styles.mono}>{i.numero}</Lien>
                        <span className={styles.sous}>{dateHeure(i.cree_le)}{i.remboursement_id ? ', remboursement' : i.annulation ? ', annulation' : ''}</span>
                      </span>
                      <span className={`${styles.montant} ${i.montant < 0 ? styles.negatif : ''}`}>{francs(i.montant)}</span>
                    </li>
                  ))}
                </ul>
              </Panneau>
            </div>
            <div className={commun.pile}>
              <Panneau titre="Actions">
                <div className={styles.actions}>
                  {p.etat === 'confirme' && droits.creer && remboursable > 0 ? (
                    <FormulaireAction<Remboursement>
                      libelle="Proposer un remboursement"
                      titre="Proposer un remboursement"
                      explication={`Au plus ${francs(remboursable)}. La proposition est validée par le financier puis ordonnée par le maire ou président (ou son délégataire), par une autre personne que le validateur.`}
                      chemin="/recettes/remboursements"
                      corps={{ paiement_id: p.id }}
                      champs={[
                        { nom: 'montant', libelle: 'Montant (F)', type: 'nombre', obligatoire: true, defaut: String(remboursable) },
                        { nom: 'motif', libelle: 'Motif', type: 'multiligne', obligatoire: true },
                        { nom: 'piece_document_id', libelle: 'Identifiant de la pièce justificative', aide: 'Document déjà déposé, facultatif' },
                      ]}
                      succes={(r) => `Remboursement ${r.numero} proposé.`}
                      apres={(r) => void navigate(`/recettes/remboursements/${r.id}`)}
                      variante="secondaire"
                    />
                  ) : null}
                  {p.etat === 'confirme' && droits.modifier ? (
                    <FormulaireAction
                      libelle="Annuler le paiement"
                      titre="Annuler le paiement"
                      explication="Réservé à un paiement confirmé non remboursé : les imputations sont reversées, la quittance est annulée avec le motif et reste vérifiable."
                      chemin={`/recettes/paiements/${p.id}/annuler`}
                      champs={[{ nom: 'motif', libelle: 'Motif', type: 'multiligne', obligatoire: true }]}
                      succes="Paiement annulé."
                      variante="danger"
                    />
                  ) : null}
                </div>
                {p.remboursements.length > 0 ? (
                  <ul className={styles.liste}>
                    {p.remboursements.map((r) => (
                      <li key={r.id}>
                        <span>
                          <Lien href={`/recettes/remboursements/${r.id}`} className={styles.mono}>{r.numero}</Lien>
                          <span className={styles.sous}>{r.motif}</span>
                        </span>
                        <span className={styles.montant}>{francs(r.montant)}</span>
                        <PuceEtat ton={tonRemboursement(r.etat)} libelle={libelle(LIBELLES_ETAT_REMBOURSEMENT, r.etat)} />
                      </li>
                    ))}
                  </ul>
                ) : null}
                {p.canal !== 'caisse' ? <StatutPrestataire id={p.id} /> : null}
              </Panneau>
              {enAttente && simulateur ? (
                <Panneau titre="Simulateur de paiement">
                  <Simulateur reference={p.reference_serveur} />
                </Panneau>
              ) : null}
            </div>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
